import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowRight, ChevronDown } from 'lucide-react';

const HeroSection = ({
  title,
  subtitle,
  ctaText = "Richiedi un preventivo personalizzato",
  ctaLink = "/preventivo",
  videoUrl,
  videoUrls = [], // Array di video per il carosello
  imageUrl,
  showScrollIndicator = true,
  overlay = 'light',
  minHeight = '100vh',
  videoDuration = 8000 // Durata di ogni video in ms prima di passare al successivo
}) => {
  const videoRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Usa videoUrls se fornito, altrimenti usa videoUrl singolo
  const videos = videoUrls.length > 0 ? videoUrls : (videoUrl ? [videoUrl] : []);
  const hasMultipleVideos = videos.length > 1;
  const currentVideo = videos[currentVideoIndex];

  // Funzione per passare al video successivo
  const nextVideo = useCallback(() => {
    if (!hasMultipleVideos) return;
    
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentVideoIndex((prev) => (prev + 1) % videos.length);
      setIsTransitioning(false);
    }, 500);
  }, [hasMultipleVideos, videos.length]);

  // Timer per cambiare video automaticamente
  useEffect(() => {
    if (!hasMultipleVideos || videoFailed) return;

    const timer = setInterval(() => {
      nextVideo();
    }, videoDuration);

    return () => clearInterval(timer);
  }, [hasMultipleVideos, videoFailed, videoDuration, nextVideo]);

  // Gestione caricamento video
  useEffect(() => {
    if (videoRef.current && currentVideo) {
      const video = videoRef.current;
      
      video.load();
      
      const handleCanPlay = () => {
        setIsLoaded(true);
        video.play().catch(() => {
          setVideoFailed(true);
        });
      };
      
      const handleError = () => {
        if (hasMultipleVideos) {
          nextVideo();
        } else {
          setVideoFailed(true);
          setIsLoaded(true);
        }
      };

      video.addEventListener('canplay', handleCanPlay);
      video.addEventListener('error', handleError);
      
      const timeout = setTimeout(() => {
        if (!isLoaded) {
          if (hasMultipleVideos) {
            nextVideo();
          } else {
            setVideoFailed(true);
            setIsLoaded(true);
          }
        }
      }, 15000);

      return () => {
        video.removeEventListener('canplay', handleCanPlay);
        video.removeEventListener('error', handleError);
        clearTimeout(timeout);
      };
    }
  }, [currentVideo, isLoaded, hasMultipleVideos, nextVideo]);

  const overlayClass = overlay === 'light' 
    ? 'bg-black/30' 
    : 'bg-black/50';

  const showVideo = currentVideo && !videoFailed;
  const showImage = imageUrl && (!currentVideo || videoFailed);

  return (
    <section 
      className="relative w-full overflow-hidden"
      style={{ minHeight }}
    >
      {/* Background Video */}
      {showVideo && (
        <video
          ref={videoRef}
          key={currentVideo}
          autoPlay
          muted
          loop={!hasMultipleVideos}
          playsInline
          preload="auto"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
            isLoaded && !videoFailed && !isTransitioning ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ objectPosition: 'center 80%' }}
        >
          <source src={currentVideo} type="video/mp4" />
        </video>
      )}

      {/* Background Image (shown when no video or video failed) */}
      {showImage && (
        <img
          src={imageUrl}
          alt=""
          onLoad={() => setIsLoaded(true)}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
        />
      )}

      {/* Placeholder while loading */}
      <div 
        className={`absolute inset-0 bg-gray-900 transition-opacity duration-1000 ${
          isLoaded ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
      />

      {/* Overlay */}
      <div className={`absolute inset-0 ${overlayClass}`} />

      {/* Video indicators (dots) */}
      {hasMultipleVideos && !videoFailed && (
        <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {videos.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                setIsTransitioning(true);
                setTimeout(() => {
                  setCurrentVideoIndex(index);
                  setIsTransitioning(false);
                }, 300);
              }}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentVideoIndex 
                  ? 'bg-white w-6' 
                  : 'bg-white/50 hover:bg-white/80'
              }`}
              aria-label={`Video ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Content */}
      <div 
        className="relative z-10 flex flex-col items-center justify-center text-center px-6 lg:px-12"
        style={{ minHeight }}
      >
        <div className="max-w-4xl mx-auto animate-fade-in">
          <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-light text-white leading-tight mb-6 tracking-tight">
            {title}
          </h1>
          {subtitle && (
            <p className="text-lg md:text-xl lg:text-2xl text-white/80 font-light mb-10 max-w-2xl mx-auto leading-relaxed">
              {subtitle}
            </p>
          )}
          {ctaText && (
            <Link to={ctaLink}>
              <Button 
                className="bg-sky-500 hover:bg-sky-600 text-white px-8 md:px-12 py-4 md:py-6 text-base md:text-lg font-medium rounded-none transition-all duration-300 hover:scale-105 group"
              >
                {ctaText}
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
              </Button>
            </Link>
          )}
        </div>

        {/* Scroll Indicator */}
        {showScrollIndicator && (
          <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
            <ChevronDown className="w-8 h-8 text-white/60" />
          </div>
        )}
      </div>
    </section>
  );
};

export default HeroSection;
